import { FC, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useIntl } from 'react-intl'

type Props = {
  /**
   * Omitted when there is nothing to retry.
   *
   * A turn can error on a message this mount never sent: backfill replays from
   * `since: 0` over a ring with no TTL, so reopening a chat whose last turn
   * errored surfaces that error with no question in hand. A Retry button that
   * sends the empty string is worse than no button — it does nothing, twice,
   * and reads as the app being broken rather than as "ask again".
   */
  onRetry?: () => void
  /**
   * The service's own reason, off the `error` event's `detail`.
   *
   * Behind a disclosure rather than inline: the strings are specific and
   * actionable — the model hitting its output limit before writing an answer,
   * for instance — but they are also raw service copy, untranslated and
   * sometimes long, so they belong one click away from a sentence the user can
   * read. Until this existed the reason was read off the wrong field and thrown
   * away entirely.
   */
  detail?: string
}

const ChatError: FC<Props> = ({ onRetry, detail }) => {
  const intl = useIntl()
  const [open, setOpen] = useState(false)

  return (
    <div className='lum-error' role='alert'>
      <AlertTriangle size={16} aria-hidden='true' />
      <span>
        {intl.formatMessage({
          id: 'Sorry, there was a problem. Please ask your question again',
        })}
      </span>
      {onRetry ? (
        <button type='button' className='lum-retry' onClick={onRetry}>
          {intl.formatMessage({ id: 'Try again' })}
        </button>
      ) : null}
      {detail ? (
        <>
          <button
            type='button'
            data-testid='chat-error-detail'
            className='lum-error-toggle'
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            {intl.formatMessage({
              id: open ? 'CHAT.ERROR.HIDE_DETAIL' : 'CHAT.ERROR.SHOW_DETAIL',
            })}
          </button>
          {open ? <p className='lum-error-detail'>{detail}</p> : null}
        </>
      ) : null}
    </div>
  )
}

export default ChatError
